import { useCallback, useMemo, useState } from "react";
import type { ChangeEvent, ComponentType, ReactNode } from "react";

import {
	AlertOutlined,
	AuditOutlined,
	ContactsOutlined,
	DownOutlined,
	ExceptionOutlined,
	FileProtectOutlined,
	FileTextOutlined,
	MailOutlined,
	MedicineBoxOutlined,
	PhoneOutlined,
	SearchOutlined,
	SendOutlined,
	SolutionOutlined,
	UpOutlined,
} from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Input, Tree, Typography } from "antd";

import subEntityStyles from "../../styles/SubEntityLayoutSidebar.module.scss";

import { PopupForm } from "#components";
import { useDefinition } from "#hooks";
import { QueryListPage } from "#layouts";

import styles from "./Dashboard.module.scss";

import type { TreeDataNode } from "antd";

// Unique icon per nav item (match image reference)
const dashboardIconMap: Record<string, ReactNode> = {
	"scriptassist-queue": <SolutionOutlined />,
	"sa-missing-demographics": <ContactsOutlined />,
	"sa-welcome-call": <PhoneOutlined />,
	"sa-inprocess-pap": <AuditOutlined />,
	"sa-scan-signed-doc": <FileProtectOutlined />,
	"sa-incoming-faxes": <MailOutlined />,
	"sa-outgoing-faxes": <SendOutlined />,
	"sa-missing-phy-sig": <ExceptionOutlined />,
	"sa-preparing-pap-sub": <FileTextOutlined />,
	"sa-pending-phy-visit": <MedicineBoxOutlined />,
	"sa-pending-340b": <AlertOutlined />,
};

// ============ Type Definitions ============

interface DashboardNavItemBase {
	key: string;
	label: string;
	icon?: ReactNode;
	badge?: number | string;
	searchAliases?: string[];
}

// Parent node with children (cannot have component)
interface DashboardNavItemWithChildren extends DashboardNavItemBase {
	children: DashboardNavItem[];
	component?: never;
	componentProps?: never;
}

// Leaf node with component
interface DashboardNavItemWithComponent extends DashboardNavItemBase {
	children?: never;
	component: ComponentType<any>;
	componentProps?:
		| Record<string, any>
		| ((definition: any) => Record<string, any>);
}

// Leaf node without component (placeholder)
interface DashboardNavItemPlaceholder extends DashboardNavItemBase {
	children?: never;
	component?: never;
	componentProps?: never;
}

type DashboardNavItem =
	| DashboardNavItemWithChildren
	| DashboardNavItemWithComponent
	| DashboardNavItemPlaceholder;

// ============ Queue Stage Mappings ============
// Each queue maps to the patient case stages it contains.
// Stages are grouped by the action an RCC needs to take.

// ============ Configuration ============

const navConfig: DashboardNavItem[] = [
	{
		key: "scriptassist-queue",
		label: "ScriptAssist Queue",
		children: [
			{
				key: "sa-missing-demographics",
				label: "Missing Demographics",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_missing_demographics",
					scope: "sa-missing-demographics",
				},
			},
			{
				key: "sa-welcome-call",
				label: "Welcome Call Needed",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_welcome_call_needed",
					scope: "sa-welcome-call",
				},
			},
			{
				key: "sa-inprocess-pap",
				label: "In-Process PAP",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_inprocess_pap",
					scope: "sa-inprocess-pap",
				},
			},
			{
				key: "sa-scan-signed-doc",
				label: "Scan Signed Document",
				searchAliases: ["Scanned Signed Document"],
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_scan_signed_doc",
					scope: "sa-scan-signed-doc",
				},
			},
			{
				key: "sa-incoming-faxes",
				label: "Incoming Faxes",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_incoming_faxes",
					scope: "sa-incoming-faxes",
				},
			},
			{
				key: "sa-outgoing-faxes",
				label: "Outgoing Faxes",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_outgoing_faxes",
					scope: "sa-outgoing-faxes",
				},
			},
			{
				key: "sa-missing-phy-sig",
				label: "Missing Physician Signature",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_missing_phy_signature",
					scope: "sa-missing-phy-sig",
				},
			},
			{
				key: "sa-preparing-pap-sub",
				label: "Preparing PAP Submission",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_preparing_pap_submission",
					scope: "sa-preparing-pap-sub",
				},
			},
			{
				key: "sa-pending-phy-visit",
				label: "Pending Physician Visit",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_pending_phy_visit",
					scope: "sa-pending-phy-visit",
				},
			},
			{
				key: "sa-pending-340b",
				label: "Pending 340B Followup",
				badge: 0,
				component: QueryListPage,
				componentProps: {
					queryCode: "queue_pending_340b_followup",
					scope: "sa-pending-340b",
				},
			},
		],
	},
];

// ============ Helper Functions ============

/**
 * Find a nav item by key in the tree structure
 */
const findNavItem = (
	items: DashboardNavItem[],
	key: string
): DashboardNavItem | undefined => {
	for (const item of items) {
		if (item.children) {
			const found = findNavItem(item.children, key);
			if (found) return found;
		} else if (!item.children && item.key === key) {
			return item;
		}
	}
	return undefined;
};

const findFirstLeafWithComponent = (
	items: DashboardNavItem[]
): DashboardNavItemWithComponent | undefined => {
	for (const item of items) {
		if (item.children) {
			const found = findFirstLeafWithComponent(item.children);
			if (found) return found;
		} else if ("component" in item && item.component) {
			return item;
		}
	}
	return undefined;
};

const collectParentKeysTo = (
	items: DashboardNavItem[],
	targetKey: string,
	chain: string[] = []
): string[] | null => {
	for (const item of items) {
		if (item.key === targetKey) {
			return chain;
		}
		if (item.children) {
			const found = collectParentKeysTo(item.children, targetKey, [
				...chain,
				item.key,
			]);
			if (found !== null) {
				return found;
			}
		}
	}
	return null;
};

const matchesNavSearch = (item: DashboardNavItem, query: string): boolean => {
	const searchableText = [
		item.key,
		item.label,
		...(item.searchAliases ?? []),
	].join(" ");
	return searchableText.toLowerCase().includes(query);
};

const filterNavItems = (
	items: DashboardNavItem[],
	searchText: string
): DashboardNavItem[] => {
	const query = searchText.trim().toLowerCase();
	if (!query) {
		return items;
	}

	const filteredItems: DashboardNavItem[] = [];
	for (const item of items) {
		const itemMatches = matchesNavSearch(item, query);
		if (item.children) {
			const filteredChildren = filterNavItems(item.children, searchText);
			if (itemMatches) {
				filteredItems.push(item);
			} else if (filteredChildren.length > 0) {
				filteredItems.push({ ...item, children: filteredChildren });
			}
			continue;
		}
		if (itemMatches) {
			filteredItems.push(item);
		}
	}
	return filteredItems;
};

const collectBranchKeys = (items: DashboardNavItem[]): string[] =>
	items.flatMap((item) =>
		item.children
			? [item.key, ...collectBranchKeys(item.children)]
			: []
	);

const defaultDashboardLeaf = findFirstLeafWithComponent(navConfig);
const defaultSelectedKey = defaultDashboardLeaf?.key ?? "";
const defaultExpandedKeys =
	collectParentKeysTo(navConfig, defaultSelectedKey) ?? [];

/**
 * Convert DashboardNavItem[] to Ant Design TreeDataNode[]
 */
const convertToTreeData = (items: DashboardNavItem[]): TreeDataNode[] =>
	items.map((item) => ({
		key: item.key,
		icon: dashboardIconMap[item.key] ?? <FileTextOutlined />,
		title: (
			<Flex
				justify="space-between"
				align="center"
				className={styles.navItemRow}
			>
				<Typography.Text className={styles.navItemLabel}>
					{item.label}
				</Typography.Text>
				{/* {item.badge !== undefined && (
					<span className={styles.navBadge}>{item.badge}</span>
				)} */}
			</Flex>
		),
		children: item.children ? convertToTreeData(item.children) : undefined,
		isLeaf: !item.children,
	}));

// ============ Main Component ============

export const Dashboard = () => {
	const definition = useDefinition();
	const navigate = useNavigate();
	const [selectedKey, setSelectedKey] = useState<string>(defaultSelectedKey);
	const [expandedKeys, setExpandedKeys] =
		useState<string[]>(defaultExpandedKeys);
	const [formOpen, setFormOpen] = useState(false);
	const [formEntityName, setFormEntityName] = useState<string | null>(null);
	const [formRecordID, setFormRecordID] = useState<string | null>(null);
	const [navSearchText, setNavSearchText] = useState("");

	const handleQueueRowClick = useCallback(
		(rowData: Record<string, unknown>) => {
			const patientID = rowData.patient_id;
			if (
				selectedKey === "sa-welcome-call" &&
				(typeof patientID === "string" || typeof patientID === "number")
			) {
				void navigate({
					to: `/patient/${String(patientID)}/welcome-call?mode=create` as "/",
				});
				return;
			}

			const entityName = rowData.entity_name;
			const entityID = rowData.entity_id;
			if (typeof entityName !== "string" || !entityName) {
				return;
			}
			if (entityID === undefined || entityID === null) {
				return;
			}
			if (typeof entityID !== "string" && typeof entityID !== "number") {
				return;
			}
			setFormEntityName(entityName);
			setFormRecordID(String(entityID));
			setFormOpen(true);
		},
		[navigate, selectedKey]
	);

	const handleQueueFormClose = useCallback(() => {
		setFormOpen(false);
		setFormEntityName(null);
		setFormRecordID(null);
	}, []);

	const filteredNavConfig = useMemo(
		() => filterNavItems(navConfig, navSearchText),
		[navSearchText]
	);

	// Convert nav config to tree data for Ant Design Tree (memoized to prevent re-renders)
	const treeData = useMemo(
		() => convertToTreeData(filteredNavConfig),
		[filteredNavConfig]
	);
	const visibleExpandedKeys = useMemo(
		() =>
			navSearchText.trim()
				? collectBranchKeys(filteredNavConfig)
				: expandedKeys,
		[expandedKeys, filteredNavConfig, navSearchText]
	);

	// Handle tree node selection
	const handleSelect = (
		selectedKeys: React.Key[],
		info: { node: { key: React.Key; children?: unknown[] } }
	) => {
		const key = info.node.key as string;

		const hasBranchChildren =
			Array.isArray(info.node.children) && info.node.children.length > 0;

		// Ant Design may set `children: []` on leaves; `[]` is truthy — only treat non-empty as expandable
		if (hasBranchChildren) {
			setExpandedKeys((prev) =>
				prev.includes(key)
					? prev.filter((k) => k !== key)
					: [...prev, key]
			);
		} else if (selectedKeys.length > 0) {
			setSelectedKey(selectedKeys[0] as string);
		}
	};

	// Handle tree node expand/collapse
	const handleExpand = (expandedKeys: React.Key[]) => {
		setExpandedKeys(expandedKeys as string[]);
	};

	// Get the selected item's content configuration
	const selectedItem = findNavItem(navConfig, selectedKey);

	// Memoize resolved props to prevent unnecessary re-renders
	const resolvedProps = useMemo(() => {
		if (!selectedItem?.componentProps) return {};

		const base =
			typeof selectedItem.componentProps === "function"
				? selectedItem.componentProps(definition)
				: selectedItem.componentProps;

		if (selectedItem.component !== QueryListPage) {
			return base;
		}

		return {
			...base,
			onRowClick: handleQueueRowClick,
		};
	}, [selectedItem, definition, handleQueueRowClick]);

	// Render content based on configuration
	const renderContent = () => {
		if (!selectedItem) {
			return (
				<Flex
					align="center"
					justify="center"
					className={styles.contentPlaceholder}
				>
					<Typography.Text type="secondary">
						Select an item from the navigation
					</Typography.Text>
				</Flex>
			);
		}

		const { component: ContentComponent } = selectedItem;

		if (!ContentComponent) {
			// No component configured - show placeholder
			return (
				<Flex
					align="center"
					justify="center"
					className={styles.contentPlaceholder}
				>
					<Typography.Text type="secondary">
						No content configured for &ldquo;{selectedItem.label}
						&rdquo;
					</Typography.Text>
				</Flex>
			);
		}

		return <ContentComponent key={selectedKey} {...resolvedProps} />;
	};

	return (
		<Flex className={styles.dashboardContainer}>
			{/* Left Panel - Navigation Tree */}
			<div className={subEntityStyles.leftNav}>
				<Flex vertical gap={8} className={styles.navPanel}>
					<Input
						allowClear
						prefix={<SearchOutlined />}
						placeholder="Search dashboard..."
						value={navSearchText}
						onChange={(event: ChangeEvent<HTMLInputElement>) => {
							setNavSearchText(event.target.value);
						}}
						className={styles.navSearch}
					/>
					{treeData.length > 0 ? (
						<Tree
							treeData={treeData}
							selectedKeys={[selectedKey]}
							expandedKeys={visibleExpandedKeys}
							onSelect={handleSelect}
							onExpand={handleExpand}
							blockNode
							showLine={false}
							switcherIcon={({
								expanded,
							}: {
								expanded?: boolean;
							}) =>
								expanded ? (
									<UpOutlined
										className={styles.switcherIcon}
									/>
								) : (
									<DownOutlined
										className={styles.switcherIcon}
									/>
								)
							}
							className={styles.navTree}
						/>
					) : (
						<Typography.Text
							type="secondary"
							className={styles.emptyNavSearch}
						>
							No dashboard items found
						</Typography.Text>
					)}
				</Flex>
			</div>

			{/* Right Panel - Content Area */}
			<div className={styles.contentPanel}>{renderContent()}</div>

			{formEntityName !== null && formRecordID !== null && (
				<PopupForm
					open={formOpen}
					entityName={formEntityName}
					mode="edit"
					recordID={formRecordID}
					onClose={handleQueueFormClose}
				/>
			)}
		</Flex>
	);
};
